// React imports
import { useState, useEffect } from 'react';

// Generic imports
import {parse} from 'csv-parse/browser/esm/sync';
import _ from 'lodash';
import OpenAI from 'openai';

// FontAwesomeIcon imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';

// MUI imports
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';

// Local imports
import { matchPort } from './RatesConvert.utils.js'
import CurrencyTextField from '../components/CurrencyTextField.js';
import PRODUCT_TYPES from '../../ProductTypes.js';

const FIELDS = {
    CAF: [{
        source: 'CAF (4.5% on FRT)',
        target: { products: '*', dangerous: '*' },
    }],
    CGS: [{
        source: 'CGS',
        target: { products: '*', dangerous: '*' },
    }],
    CSF: [{
        source: 'CSF',
        target: { products: '*', dangerous: '*' },
    }],
    SCS: [{
        source: 'SCS',
        target: { products: '*', dangerous: '*' },
    }],
    WAR: [{
        source: 'WAR',
        target: { products: '*', dangerous: '*' },
    }],
    'RPT (paid collect)': [{
        source: 'RPT (paid collect)',
        target: { products: '*', dangerous: '*' },
    }],
    'Origin THC': [{
        source: 'Origin THC',
        target: { products: [ '20\' Dry', '20\' Reefer', '40\' Dry', '40\' HC Dry', '40\' HC Reefer' ], dangerous: false },
    }, {
        source: 'Origin THC for HAZ / FL / OT',
        target: { products: [ '20\' Dry', '20\' Reefer', '40\' Dry', '40\' HC Dry', '40\' HC Reefer' ], dangerous: true },
    }, {
        source: 'Origin THC for HAZ / FL / OT',
        target: { products: [ '20\' Flat', '20\' Open Top', '40\' Flat', '40\' Open Top', '40\' HC Flat', '40\' HC Open Top' ], dangerous: '*' },
    }],
    'BRC': [{
        source: 'BRC (vatos)',
        target: { products: '*', dangerous: '*' },
    }],
    'ETS': [{
        source: 'ETS (vatos)',
        target: { products: '*', dangerous: '*' },
    }],
    'SPD': [{
        source: 'SPD',
        target: { products: '*', dangerous: '*' },
    }],
    'CAC': [{
        source: 'CAC',
        target: { products: '*', dangerous: '*' },
    }],
    'OTS': [{
        source: 'OTS (Open Top) / SEQ (for Flat)',
        target: { products: [ '20\' Open Top', '40\' Open Top', '40\' HC Open Top' ], dangerous: '*' },
    }],
    'SEQ': [{
        source: 'OTS (Open Top) / SEQ (for Flat)',
        target: { products: [ '20\' Flat', '40\' Flat', '40\' HC Flat' ], dangerous: '*' },
    }],
}

function RatesConvertMSC(props) {
    const { data, formObj, supplier, supplierEmail, seaPorts, onDone, autoskip, organization } = props;
    const [ parsed, setParsed ] = useState(null);
    const [ dialogStage, setDialogStage ] = useState({});

    useEffect(() => {
        (async () => {
            setParsed(await parseMsc({ data, supplier, supplierEmail, formObj, seaPorts }));
            setDialogStage({ fileIndex: 0, stage: 'POL' });
        })();
    }, []);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px' }}>
            <CircularProgress />

            {dialogStage.stage === 'POL' && (
                <POLDialog 
                    data={parsed[dialogStage.fileIndex].file.header} 
                    POLs={parsed[dialogStage.fileIndex].POLs}
                    validFrom={parsed[dialogStage.fileIndex].validFrom}
                    validUntil={parsed[dialogStage.fileIndex].validUntil}
                    fileCount={`${dialogStage.fileIndex + 1} / ${parsed.length}`}
                    autoskip={autoskip}
                    onDone={({ POLs, validFrom, validUntil }) => {
                        const p = _.cloneDeep(parsed);
                        p[dialogStage.fileIndex].POLs = POLs;
                        p[dialogStage.fileIndex].validFrom = validFrom;
                        p[dialogStage.fileIndex].validUntil = validUntil;
                        setParsed(p);
                        setDialogStage({...dialogStage, pricesIndex: 0, stage: 'POD' });
                    }}
                />
            )}

            {dialogStage.stage === 'POD' && (
                <PODDialog
                    fileCount={`${dialogStage.fileIndex + 1} / ${parsed.length} Table: ${dialogStage.pricesIndex + 1} / ${parsed[dialogStage.fileIndex].prices.length}`}
                    data={parsed[dialogStage.fileIndex].file.prices[dialogStage.pricesIndex].routes}
                    destinations={parsed[dialogStage.fileIndex].prices[dialogStage.pricesIndex].destinations}
                    autoskip={autoskip}
                    onDone={PODs => {
                        const p = _.cloneDeep(parsed);
                        p[dialogStage.fileIndex].prices[dialogStage.pricesIndex].destinations = PODs;
                        setParsed(p);
                        setDialogStage({...dialogStage, stage: 'costs' });
                    }}
                />
            )}

            {dialogStage.stage === 'costs' && (
                <CostsDialog
                    fileCount={`${dialogStage.fileIndex + 1} / ${parsed.length} Table: ${dialogStage.pricesIndex + 1} / ${parsed[dialogStage.fileIndex].prices.length}`}
                    data={parsed[dialogStage.fileIndex].file.prices[dialogStage.pricesIndex].prices}
                    FRTs={parsed[dialogStage.fileIndex].prices[dialogStage.pricesIndex].FRTs}
                    destinations={parsed[dialogStage.fileIndex].prices[dialogStage.pricesIndex].destinations}
                    autoskip={autoskip}
                    organization={organization}
                    onDone={costs => {
                        const p = _.cloneDeep(parsed);
                        p[dialogStage.fileIndex].prices[dialogStage.pricesIndex].prices = costs;
                        setParsed(p);

                        const nextPrices = dialogStage.pricesIndex + 1;
                        if (nextPrices === parsed[dialogStage.fileIndex].file.prices.length) {
                            const nextFile = dialogStage.fileIndex + 1;
                            if (nextFile === parsed.length) {

                                const results = [];
                                for (let fileIndex = 0 ; fileIndex < parsed.length ; fileIndex++) {
                                    const { validFrom, validUntil, POLs, prices } = p[fileIndex];
                                    POLs.forEach(port => {
                                        prices.forEach(({ destinations, FRTs, prices }) => {
                                            destinations.forEach(({ POD, transshipment, transitTime }, destinationIndex) => {
                                                PRODUCT_TYPES['sea'].forEach(product => {
                                                    [true, false].forEach(dangerous => {
                                                        if (product.name.startsWith('By')) return;
                                                        results.push({
                                                            source: port.code,
                                                            destination: POD.code,
                                                            transportationMethod: 'sea',
                                                            product: product.name,
                                                            dangerous,
                                                            supplier,
                                                            supplierEmail,
                                                            form: formObj.title,
                                                            validFrom,
                                                            validUntil,
                                                            transshipment,
                                                            transitTime,
                                                            FRT: `${product.name.startsWith('40\' HC') ? FRTs[destinationIndex].p40HC : product.name.startsWith('40\'') ? FRTs[destinationIndex].p40 : FRTs[destinationIndex].p20}|${FRTs[destinationIndex].currency}`,
                                                            ..._.mapValues(prices, c => {
                                                                if (!(c[product.name] || {})[dangerous]) return '';
                                                                return `${c[product.name][dangerous].value}|${c[product.name][dangerous].currency}`;
                                                            })
                                                        })
                                                    });
                                                });
                                            });
                                        });
                                    });
                                }
                                onDone(results);
                                setDialogStage({stage: 'done' });
                            } else {
                                setDialogStage({...dialogStage, fileIndex: nextFile, stage: 'POL' });
                            }
                        } else {
                            setDialogStage({...dialogStage, stage: 'POD', pricesIndex: nextPrices });
                        }
                    }}
                />
            )}
        </Box>
    )
}

function PODDialog(props) {
    const { data, destinations, onDone, fileCount, autoskip } = props;
    const [ local, setLocal ] = useState(null);

    useEffect(() => {
        setLocal(_.clone(destinations));

        if (autoskip) {
            setTimeout(() => onDone(_.clone(destinations)), 100);
        }
    }, []);

    return (
        <Dialog
            fullWidth={true}
            open={true}
            maxWidth={'xl'}
        >
            <DialogTitle>Validate Parsing - PORTS OF DESTINATION (File {fileCount})</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ padding: '12px 0px' }}>
                    Please ensure the following parse is correct:
                </DialogContentText>
                <Box sx={{ display: 'flex', padding: '0 6px' }}>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ fontWeight: 800 }}>Source</Box>
                        <Table size={'small'} >
                            <TableBody>
                                {data.map(row => (
                                    <TableRow key={JSON.stringify(row)}>
                                        {compactFromEnd(row.split(',')).map(cell => (
                                            <TableCell key={cell} sx={{ whiteSpace: 'nowrap' }}>{cell}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                    <Box sx={{ flex: 1, padding: '0px 6px' }}>
                        <Box sx={{ fontWeight: 800 }}>Result</Box>
                        {(local || []).map((destination, i) => (
                            <MenuItem key={(destination.POD || {}).id} sx={{ display: 'flex' }}>
                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                    <Box>
                                        {(destination.POD || {}).code} - {(destination.POD || {}).portname}
                                    </Box>
                                    <TextField
                                        sx={{ marginLeft: '12px' }}
                                        label='Transshipment Port'
                                        value={destination.transshipment || ''}
                                        onChange={e => {
                                            const l = _.cloneDeep(local);
                                            l[i].transshipment = e.target.value;
                                            setLocal(l);
                                        }}
                                    />
                                    <TextField
                                        sx={{ marginLeft: '12px' }}
                                        label='Transit Time'
                                        value={destination.transitTime || ''}
                                        onChange={e => {
                                            const l = _.cloneDeep(local);
                                            l[i].transitTime = e.target.value;
                                            setLocal(l);
                                        }}
                                    />
                                </Box>
                                <IconButton
                                    color='error'
                                    onClick={e => {
                                        const l = _.cloneDeep(local);
                                        _.pullAt(l, [i]);
                                        setLocal(l);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faCircleXmark} />
                                </IconButton>
                            </MenuItem>
                        ))}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onDone(local)} autoFocus>
                    Agree
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function CostsDialog(props) {
    const { data, destinations, FRTs, onDone, fileCount, autoskip, organization } = props;
    const [ local ] = useState((() => {
        const _local = {};
        _.forEach(FIELDS, (sources, field) => {
            _.forEach(sources, ({ source, target }) => {
                const line = data.find(l => (_.trim(l.split(',')[0]) === source));
                if (!line) return;
                const parts = line.split(',');
                const products = target.products === '*' ? PRODUCT_TYPES['sea'].map(p => p.name) : target.products;
                const dangerous = target.dangerous === '*' ? [false, true] : [target.dangerous];
                products.forEach(product => dangerous.forEach(d => {
                    _local[field] = _local[field] || {};
                    _local[field][product] = _local[field][product] || {};
                    _local[field][product][d] = _local[field][product][d] || {};
                    _local[field][product][d].value = parseFloat(parts[product.startsWith('40\' HC') ? 4 : product.startsWith('40\'') ? 3 : 2]);
                    _local[field][product][d].currency = parts[1];
                }));
            });
        });

        if (autoskip) {
            setTimeout(() => onDone(_local), 10);
        }
        return _local;
    })());

    return (
        <Dialog
            fullWidth={true}
            open={true}
            maxWidth={'xl'}
        >
            <DialogTitle>Validate Parsing - COSTS (File {fileCount})</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ padding: '12px 0px' }}>
                    Please ensure the following parse is correct:
                </DialogContentText>
                <Box sx={{ display: 'flex', padding: '0 6px' }}>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ fontWeight: 800 }}>Source</Box>
                        <Table size={'small'} >
                            <TableBody>
                                {data.map(row => (
                                    <TableRow key={row}>
                                        {compactFromEnd(row.split(',')).map(cell => (
                                            <TableCell key={cell} sx={{ whiteSpace: 'nowrap' }}>{cell}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                    <Box sx={{ flex: 1, padding: '0px 6px', overflowY: 'auto', maxHeight: 'calc(100vh - 235px)' }}>
                        <Box sx={{ fontWeight: 700 }}>
                            FRT (Freight)
                        </Box>
                        {destinations.map((destination, i) => (
                            <MenuItem key={(destination.POD || {}).id} sx={{ display: 'flex', alignItems: 'start', flexDirection: 'column' }}>
                                <Box sx={{ paddingLeft: '12px' }}>
                                    {destination.POD.code}
                                </Box>
                                <Box sx={{ display: 'flex' }}>
                                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', marginLeft: '24px' }}>
                                        <Box sx={{ marginRight: '12px' }}>
                                            20':
                                        </Box>
                                        <CurrencyTextField
                                            value={FRTs[i].p20}
                                            currency={FRTs[i].currency}
                                            organization={organization}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', marginLeft: '24px' }}>
                                        <Box sx={{ marginRight: '12px' }}>
                                            40':
                                        </Box>
                                        <CurrencyTextField
                                            value={FRTs[i].p40}
                                            currency={FRTs[i].currency}
                                            organization={organization}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', marginLeft: '24px' }}>
                                        <Box sx={{ marginRight: '12px' }}>
                                            40' HC:
                                        </Box>
                                        <CurrencyTextField
                                            value={FRTs[i].p40HC}
                                            currency={FRTs[i].currency}
                                            organization={organization}
                                        />
                                    </Box>
                                </Box>
                            </MenuItem>
                        ))}
                        {!local && <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></Box>}
                        {local && _.map(local, (v, field) => (
                            <Box>
                                <Box sx={{ fontWeight: 800 }}>
                                    {field}
                                </Box>
                                {_.flatten(_.map(v, (v2, product) => _.map(v2, (value, dangerous) => (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ marginLeft: '24px', marginRight: '12px' }}>
                                            {product}{dangerous === 'true' ? ' (Dangerous)' : ''}
                                        </Box>
                                        <CurrencyTextField
                                            value={value.value}
                                            currency={value.currency}
                                            organization={organization}
                                        />
                                    </Box>
                                ))))}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onDone(local)} disabled={!local} autoFocus>
                    Agree
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function POLDialog(props) {
    const { data, POLs, validFrom, validUntil, onDone, fileCount, autoskip } = props;
    const [ local, setLocal ] = useState(_.cloneDeep({ POLs, validFrom, validUntil }));

    useEffect(() => {
        if (autoskip) {
            setTimeout(() => onDone(_.cloneDeep({ POLs, validFrom, validUntil })), 100);
        }
    }, []);

    return (
        <Dialog
            fullWidth={true}
            open={true}
            maxWidth={'xl'}
        >
            <DialogTitle>Validate Parsing - PORT OF LOADING (File: {fileCount})</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ padding: '12px 0px' }}>
                    Please ensure the following parse is correct:
                </DialogContentText>
                <Box sx={{ display: 'flex', padding: '0 6px' }}>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ fontWeight: 800 }}>Source</Box>
                        <Table size={'small'} >
                            <TableBody>
                                {data.map(row => (
                                    <TableRow key={JSON.stringify(row)}>
                                        {compactFromEnd(row.split(',')).map(cell => (
                                            <TableCell key={cell} sx={{ whiteSpace: 'nowrap' }}>{cell}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                    <Box sx={{ flex: 1, padding: '0px 6px' }}>
                        <Box sx={{ fontWeight: 800 }}>Ports</Box>
                        {local.POLs.map((pol, i) => (
                            <MenuItem key={pol.id} sx={{ display: 'flex' }}>
                                <Box sx={{ flex: 1 }}>
                                    {pol.code} - {pol.portname}
                                </Box>
                                <IconButton
                                    color='error'
                                    onClick={e => {
                                        const l = _.cloneDeep(local);
                                        _.pullAt(l.POLs, [i]);
                                        setLocal(l);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faCircleXmark} />
                                </IconButton>
                            </MenuItem>
                        ))}
                        <Box sx={{ display: 'flex' }}>
                            <TextField
                                sx={{ flex: 1, marginRight: '3px' }}
                                value={local.validFrom}
                                onChange={e => setLocal({ ...local, validFrom: e.target.value })}
                                label='Valid From'
                            />
                            <TextField
                                sx={{ flex: 1, marginLeft: '3px' }}
                                value={local.validUntil}
                                onChange={e => setLocal({ ...local, validUntil: e.target.value })}
                                label='Valid Until'
                            />
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onDone(local)} autoFocus>
                    Agree
                </Button>
            </DialogActions>
        </Dialog>
    );
}

const PRICE_SKIP_PREFIXES = [
    'Advertised routings',
    'Port of Origin',
    'Trade,',
    'Trade ,',
    'Service reference',
    'Reference number',
]

async function parseMsc({ data, formObj, supplier, supplierEmail, seaPorts }) {
    const lines = data.split('\r\n');

    const files = [];
    let parts = {};
    let _lines = [];
    let mode = null;

    while (lines.length > 0) {
        const line = lines.shift();

        if ((line.startsWith('Country,')) || (line.startsWith('Remarks:,')) || (line.startsWith('Date'))) {
            if (mode === 'start') {
                parts.header = _lines;
                mode = 'prices';
            } else if (mode === 'prices') {
                parts.prices = parts.prices || [];
                const routes = [];
                const prices = [];
                const currencyPosition = _lines[0].split(',').indexOf('Currency');
                _lines.forEach(line => {
                    if (_.every(PRICE_SKIP_PREFIXES, skip => line.indexOf(skip) === -1)) {
                        const parts = line.split(',');
                        const routeLine = parts.slice(0, currencyPosition - 1);
                        if (_.some(routeLine, r => !!r)) {
                            routes.push(routeLine.join(','));
                        }
                        prices.push(parts.slice(currencyPosition - 1).join(','));
                    }
                });
                parts.prices.push({ routes, prices });
                if (line.startsWith('Remarks:,')) mode = 'remarks';
            } else if (mode === 'remarks') {
                parts.remards = _lines;
            }
            _lines = [];
        }

        if (line.startsWith('Date')) {
            if (mode && parts.prices && parts.header) files.push(parts);
            parts = {};
            mode = 'start';
        }

        _lines.push(line);
    }

    if (mode === 'remarks') {
        parts.remards = _lines;
    }
    if (mode && parts.prices && parts.header) files.push(parts);

    const openai = new OpenAI({
        apiKey: process.env.OPENAI_API_KEY,
        dangerouslyAllowBrowser: true
    });

    const results = await Promise.all(files.map((file, index) => (async () => {

        const r = await openai.chat.completions.create({
            messages: [{ role: 'user', content: `See the following table, and extract the ports of origin as a simple JSON array (split with any non letter character), the date, and the end date. Put the results in an object structured as "ports", "validFrom" and "validUntil" fields. The "validFrom" and "validUntil" fields structure as "YYYY-MM-DD". Dont add any text, return just the JSON object: ${file.header.join('\n')}` }],
            model: 'gpt-4.1-mini',
        });
        let POLs = [];
        let validFrom = '';
        let validUntil = '';
        try {
            const data = JSON.parse(r.choices[0].message.content);
            POLs = parsePOLs(data.ports, seaPorts);
            validFrom = data.validFrom || '';
            validUntil = data.validUntil || '';
        } catch(e) {}

        const prices = [];
        for (let i = 0 ; i < file.prices.length ; i++) {
            prices.push({
                destinations: await parseDestinations({ table: file.prices[i].routes, POLs, seaPorts })
            });
        }

        for (let i = 0 ; i < file.prices.length ; i++) {
            const FRTPrices = file.prices[i].prices.filter(p => p.startsWith('FRT,') || p.startsWith('Freight,'));
            prices[i].FRTs = [];
            for (let j = 0 ; j < prices[i].destinations.length ; j++) {
                const data = (FRTPrices.length >= j ? compactFromEnd((FRTPrices[0] || '').split(',')) : compactFromEnd((FRTPrices[j] || '').split(','))).map(a => _.trim(a));
                prices[i].FRTs.push({
                    currency: data[1],
                    p20: data[2],
                    p40: data[3],
                    p40HC: data[4],
                });
            }
        }

        return {
            file,
            POLs,
            validFrom,
            validUntil,
            prices,
        }

    })()));

    return results;
}

function parsePOLs(pols, seaPorts) {
    return _.compact(pols.map(p => {
        const source = matchPort({ name: p, seaPorts });
        if (!source) return null;
        return source;
    }));
}

async function parseDestinations({ table, POLs, seaPorts }) {
    const result = [];

    const csv = parse(table.join('\r\n'), {
        columns: true,
    });

    for await (let record of csv) {
        record = _.mapKeys(record, (v, k) => _.trim(k));

        const portOfDestination = record['Port of Destination'] || record['Ports of Destination'];
        const POD = matchPort({ name: portOfDestination, seaPorts });
        if (!POD) continue;

        const transshipment = record['T/S Port'];
        const transitTime = record['Transit Time from last Israeli port'] || record['Estimate Transit Time from last Israeli port'] || record['Estimated Transit Time from last Israeli port'];

        result.push({ POD, transshipment, transitTime });
    }

    return result;
}

function compactFromEnd(array) {
    const arr = _.cloneDeep(array);
    while (arr.length > 0 && !arr[arr.length - 1]) arr.pop();
    return arr;
}

export default RatesConvertMSC;
